/**
 * Configurable Branding System (DC-1297)
 *
 * Provides centralized branding configuration for customer deployments.
 * Reads from window.__COMPASS_CONFIG__ (injected by server) with defaults.
 *
 * Usage:
 *   import { BRANDING } from '../config/branding';
 *   <h1>{BRANDING.productName}</h1>
 */

// ============================================================================
// GLOBAL TYPE AUGMENTATION
// ============================================================================

interface CompassBrandingConfig {
  product_name?: string;
  customer_name?: string;
  tagline?: string;
  support_email?: string;
  support_url?: string;
}

interface CompassWindowConfig {
  branding?: CompassBrandingConfig;
  features?: Record<string, boolean>;
}

declare global {
  interface Window {
    __COMPASS_CONFIG__?: CompassWindowConfig;
  }
}

// ============================================================================
// BRANDING CONFIGURATION
// ============================================================================

function getConfig(): CompassWindowConfig {
  if (typeof window !== 'undefined' && window.__COMPASS_CONFIG__) {
    return window.__COMPASS_CONFIG__;
  }
  return {};
}

export const BRANDING = {
  /** Product name displayed throughout the UI */
  get productName(): string {
    return getConfig().branding?.product_name || 'DATA Compass';
  },

  /** Optional customer name (e.g., "Amgen" for white-label) */
  get customerName(): string {
    return getConfig().branding?.customer_name || '';
  },

  /** Tagline shown on login/landing pages */
  get tagline(): string {
    return getConfig().branding?.tagline || 'AI Readiness Platform for Life Sciences';
  },

  /** Support email for help text */
  get supportEmail(): string {
    return getConfig().branding?.support_email || '';
  },

  /** Support URL for help links */
  get supportUrl(): string {
    return getConfig().branding?.support_url || '';
  },

  /** Full title: "ProductName - Tagline" */
  get fullTitle(): string {
    return `${BRANDING.productName} - ${BRANDING.tagline}`;
  },
};

export default BRANDING;
